import { controlClass } from '@/components/Field';
import {
  companyDateFormat,
  dateFormats,
  defaultDateFormat,
  setUserDateFormat,
  userDateFormat,
} from '@core/format/datePreference';
import { useDateFormat } from '@/format/useDateFormat';
import type { AccessibleBranch, Session } from '@core/session/types';
import {
  accentOptions,
  companyAccentChoice,
  defaultAccent,
  setUserAccent,
  useAccent,
  userAccentChoice,
  type Accent,
} from './accent';
import { ContextStrip, SubscriptionNotice } from './ContextStrip';
import type { LayoutState } from './layoutState';
import { closeOpenDropdowns } from './preline';
import {
  companyTextScale,
  defaultTextScale,
  setUserTextScale,
  textScaleOptions,
  useTextScale,
  userTextScale,
  type TextScale,
} from './textScale';

/**
 * The topbar.
 *
 * The template's `navbar-header`, carrying our things: the branch and financial-year context on the
 * left, and on the right the two account actions that are deliberately not in the sidebar. The
 * sidebar is a projection of permissions; changing your own password and enrolling your own second
 * factor are not permissioned and must not be — an administrator should not be able to revoke a
 * user's ability to secure their own sign-in, and no plan tier should be able to withhold it.
 */
export function Topbar({
  session,
  layout,
  onBranchChange,
  onSignOut,
  onChangePassword,
  onSecurity,
  onFind,
}: {
  session: Session;
  layout: LayoutState;
  onBranchChange: (branch: AccessibleBranch) => void;
  onSignOut: () => void;
  onChangePassword: () => void;
  onSecurity: () => void;
  /**
   * Opens the global find.
   *
   * <p>
   * <strong>Raised as an intent rather than rendered here, and that is a fix rather than a
   * preference.</strong> Every app overlay in this product sits at `z-9999`, the same rung as the
   * permanently `z-9999` sidebar, so which one covers the other is settled by DOM order — and
   * `AppShell` renders the sidebar <em>after</em> the topbar. A panel mounted from here therefore
   * lost to the sidebar, leaving it bright and clickable beside a supposedly modal dialog. Found by
   * hit-testing `elementsFromPoint` over the sidebar in a real browser, which is the only check
   * that can see it.
   * </p>
   */
  onFind: () => void;
}) {
  return (
    <header className="navbar-header flex items-center max-lg:w-full">
      <div className="topbar-menu flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <button
            id="mobile_btn"
            type="button"
            className="mobile-btn lg:hidden"
            aria-label="Open menu"
            onClick={layout.openMobileMenu}
          >
            <i className="icon icon-menu" />
          </button>

          {/* The rail toggle lives here as well as in the sidebar, because in the rail state the
              sidebar's own toggle is 48px wide and the user has just made the thing they need to
              click as small as it goes. */}
          <button
            id="toggle_btn2"
            type="button"
            className="sidenav-toggle-btn topbar-link size-9 shrink-0 items-center justify-center rounded-full text-[20px] max-lg:hidden"
            aria-label={layout.miniSidebar ? 'Expand menu' : 'Collapse menu'}
            onClick={layout.toggleMiniSidebar}
          >
            <i className="ph-duotone ph-arrow-left" />
          </button>
        </div>

        <div className="flex items-center gap-2 lg:w-full">
          <ContextStrip session={session} onBranchChange={onBranchChange} />

          <SubscriptionNotice session={session} />

          <ThemeToggle layout={layout} />

          {/* Live as of §4 of the counter redesign. It was a disabled button captioned "Search
              arrives with the catalogue" — and the search it was waiting for turned out to have
              been on the server the whole time, with nothing asking it. */}
          <button
            type="button"
            className="topbar-link relative flex size-9 items-center justify-center rounded-full max-sm:hidden"
            aria-label="Find a booking or a garment"
            title="Find a booking or a garment"
            onClick={onFind}
          >
            <i className="icon icon-search text-[18px]" />
          </button>

          {/* Still a placeholder, and honestly so: a control that looks live and does nothing is
              worse than one that says it is not ready.

              Stood down below `sm`, which is the other half of making the branch switcher visible
              on a phone. A disabled 36px button plus its gap is room a 375px topbar is spending on
              something that cannot be pressed. */}
          <button
            type="button"
            className="topbar-link relative flex size-9 items-center justify-center rounded-full max-sm:hidden"
            aria-label="Alerts — not built yet"
            title="Alerts arrive with the notification engine"
            disabled
          >
            <i className="icon icon-bell text-[18px]" />
          </button>

          <AccountMenu
            session={session}
            onSignOut={onSignOut}
            onChangePassword={onChangePassword}
            onSecurity={onSecurity}
          />
        </div>
      </div>
    </header>
  );
}

/**
 * Light/dark.
 *
 * The template ships a full dark palette — a second `@theme` block redefining every token — and it
 * would be a waste to vendor that and leave it unreachable. Counter staff work under shop lights at
 * ten at night during wedding season; this is not decoration for them.
 */
function ThemeToggle({ layout }: { layout: LayoutState }) {
  const dark = layout.theme === 'dark';

  return (
    <button
      type="button"
      className="topbar-link flex size-9 items-center justify-center rounded-full"
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      aria-pressed={dark}
      onClick={layout.toggleTheme}
    >
      <i className={`icon ${dark ? 'icon-sun' : 'icon-moon'} text-[18px]`} />
    </button>
  );
}

function AccountMenu({
  session,
  onSignOut,
  onChangePassword,
  onSecurity,
}: {
  session: Session;
  onSignOut: () => void;
  onChangePassword: () => void;
  onSecurity: () => void;
}) {
  const { user } = session;

  const choose = (action: () => void) => () => {
    // The shell survives every one of these actions except sign-out, so the menu has to be told to
    // close. Sign-out is included rather than special-cased: a menu left open behind the sign-in
    // screen reappears, still open, for whoever signs in next.
    closeOpenDropdowns();
    action();
  };

  return (
    <div className="profile-dropdown hs-dropdown relative inline-flex [--auto-close:inside] [--placement:bottom-right]">
      <button
        type="button"
        className="hs-dropdown-toggle grid size-8 cursor-pointer place-items-center rounded-md bg-dark text-[11.5px] font-semibold tracking-wide text-white focus:outline-hidden"
        aria-haspopup="menu"
        aria-expanded="false"
        aria-label="Account"
      >
        {initials(user.fullName)}
      </button>

      <div
        className="hs-dropdown-menu duration z-1 mt-2 hidden min-w-60 rounded-md border border-border-color bg-white p-2 opacity-0 shadow transition-[opacity,margin] hs-dropdown-open:opacity-100"
        role="menu"
        aria-orientation="vertical"
      >
        <div className="border-b border-border-color px-2 pb-2">
          <p className="text-sm font-semibold text-gray-900">{user.fullName}</p>
          {/* Their username, not their address: it is what they signed in with, and the only one
              of the two guaranteed to exist. */}
          <p className="font-mono-nums text-[12px] text-default">{user.username}</p>
        </div>

        <div className="flex items-center justify-between border-b border-border-color px-2 py-2">
          <span className="text-[12px] text-default">Plan</span>
          <span className="rounded bg-light px-2 py-0.5 text-[11px] text-gray-900">
            {user.entitlements.planCode ?? '—'}
          </span>
        </div>

        <DateFormatChoice />
        <TextSizeChoice />

        <AccentChoice />

        <div className="space-y-1 pt-2">
          <MenuAction icon="icon-key" label="Change password" onClick={choose(onChangePassword)} />

          {/* Labelled with its state rather than as a bare "Security", so an owner who has never
              turned it on is told so every time they open this menu instead of having to go and
              look. It is the only entry here whose answer changes anything about signing in. */}
          <MenuAction icon="icon-shield" label="Two-factor" onClick={choose(onSecurity)}>
            <span
              className={`ms-auto rounded px-1.5 py-0.5 text-[10px] ${
                user.twoFactorEnabled
                  ? 'bg-success-transparent text-success'
                  : 'bg-light text-default'
              }`}
            >
              {user.twoFactorEnabled ? 'On' : 'Off'}
            </span>
          </MenuAction>

          <MenuAction icon="icon-log-out" label="Sign out" onClick={choose(onSignOut)} danger />
        </div>
      </div>
    </div>
  );
}

/**
 * This person's own date format, for this device.
 *
 * <p>
 * Here rather than on the settings screen because it is not permissioned and must not be: the
 * settings screen is behind `Tenant:Edit`, which a counter clerk does not hold, and how a date reads
 * to the person reading it is not something an administrator needs to decide for them. The company's
 * choice is the default they are overriding, exactly as the theme toggle beside it overrides
 * nothing at all — same standing, same lack of a permission.
 * </p>
 *
 * <p>
 * A `select` rather than a `MenuAction`, because this is a choice and not an action, and because the
 * six options with their samples are the whole explanation of what is being chosen. It is deliberately
 * not a link to a preferences screen: one dropdown does not need one.
 * </p>
 */
function DateFormatChoice() {
  // Subscribed rather than merely read — this control displays the value it sets, so without a
  // subscription it would go on showing the old one immediately after being used. The value itself
  // is not needed here; the two reads below are the specific halves this renders.
  useDateFormat();

  const mine = userDateFormat();

  // What clearing the override would land on. Falling back to the *resolved* format would be wrong
  // in exactly the case this label exists for: with an override set and no company choice, the
  // resolved format is the override, and the row would offer to return you to where you already are.
  const fallback = companyDateFormat() ?? defaultDateFormat;

  return (
    <div
      className="border-b border-border-color px-2 pb-2"
      /*
        Preline closes this dropdown on any click inside it (`--auto-close:inside` on the wrapper),
        which would shut the menu the instant the select was opened. Stopping the click here keeps
        the choice usable; every other row in the menu still closes on click, which is what it should
        do — those navigate away.
      */
      onClick={(event) => event.stopPropagation()}
    >
      <label className="mb-1 block text-[12px] text-default" htmlFor="account-date-format">
        Date format
      </label>

      <select
        id="account-date-format"
        className={`${controlClass} text-[13px]`}
        value={mine ?? ''}
        onChange={(event) => setUserDateFormat(event.target.value || null)}
      >
        {/* The fallback named on the option rather than left as a bare "Default", so the person can
            see what they would be going back to before they go back to it. */}
        <option value="">Company default — {sampleFor(fallback) ?? fallback}</option>

        {dateFormats.map((format) => (
          <option key={format.value} value={format.value}>
            {format.sample}
          </option>
        ))}
      </select>

      {mine !== null && <p className="mt-1 text-[11px] text-default">Only on this device.</p>}
    </div>
  );
}

/** What a pattern renders, for naming a choice without recomputing it from today's date. */
function sampleFor(format: string | null): string | null {
  return dateFormats.find((known) => known.value === format)?.sample ?? null;
}

/**
 * How large this person wants the product, on this device.
 *
 * <p>
 * Beside the date format and built the same way, for the same reason: it is a choice, the three
 * options with their hints are the whole explanation, and one dropdown does not need a preferences
 * screen. <strong>It belongs here as well as on the settings card</strong> — the company sets a
 * house size, but eyesight is not a company-wide fact, and the person who needs the largest step is
 * rarely the person holding `Tenant:Edit`.
 * </p>
 */
function TextSizeChoice() {
  // Subscribed for the reason above it is: this control displays the value it sets.
  useTextScale();

  const mine = userTextScale();

  // What clearing would land on — the company's, not the resolved value, which with an override and
  // no company choice would offer to return you to where you already are.
  const fallback = companyTextScale() ?? defaultTextScale;

  return (
    <div
      className="border-b border-border-color px-2 pb-2"
      // As above: Preline would close the menu the instant the select was opened.
      onClick={(event) => event.stopPropagation()}
    >
      <label className="mb-1 block text-[12px] text-default" htmlFor="account-text-scale">
        Text size
      </label>

      <select
        id="account-text-scale"
        className={`${controlClass} text-[13px]`}
        value={mine ?? ''}
        onChange={(event) => setUserTextScale((event.target.value || null) as TextScale | null)}
      >
        <option value="">Company default — {labelFor(fallback)}</option>

        {textScaleOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} — {option.hint}
          </option>
        ))}
      </select>

      {mine !== null && <p className="mt-1 text-[11px] text-default">Only on this device.</p>}
    </div>
  );
}

/** The word for a step, so the fallback row names it rather than printing the stored key. */
function labelFor(scale: string): string {
  return textScaleOptions.find((option) => option.value === scale)?.label ?? scale;
}

/**
 * The brand colour, for this device.
 *
 * <p>
 * Beside the text size and built the same way. It is here as well as on the settings card for a
 * different reason from that one, though: the size is about eyesight and this is about taste, and
 * the person who has to look at the screen all day is not always the person who chose the company's
 * colour. Clearing it follows the company again.
 * </p>
 *
 * <p>
 * Three options and no more, deliberately — every extra one is another pair of contrast ratios to
 * re-check on every screen, forever. See `shell/accent.ts`.
 * </p>
 */
function AccentChoice() {
  // Subscribed for the reason the size above is: this control displays the value it sets.
  useAccent();

  const mine = userAccentChoice();

  // What clearing would land on — the company's, not the resolved value.
  const fallback = companyAccentChoice() ?? defaultAccent;

  return (
    <div
      className="border-b border-border-color px-2 pb-2"
      // As above: Preline would close the menu the instant the select was opened.
      onClick={(event) => event.stopPropagation()}
    >
      <label className="mb-1 block text-[12px] text-default" htmlFor="account-accent">
        Theme
      </label>

      <select
        id="account-accent"
        className={`${controlClass} text-[13px]`}
        value={mine ?? ''}
        onChange={(event) => setUserAccent((event.target.value || null) as Accent | null)}
      >
        <option value="">Company default — {accentLabelFor(fallback)}</option>

        {accentOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} — {option.hint}
          </option>
        ))}
      </select>

      {mine !== null && <p className="mt-1 text-[11px] text-default">Only on this device.</p>}
    </div>
  );
}

/** The word for an accent, so the fallback row names it rather than printing the stored key. */
function accentLabelFor(accent: string): string {
  return accentOptions.find((option) => option.value === accent)?.label ?? accent;
}

function MenuAction({
  icon,
  label,
  onClick,
  danger = false,
  children,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-light ${
        danger ? 'text-danger' : 'text-gray-900'
      }`}
      onClick={onClick}
    >
      <i className={`icon ${icon} text-[15px]`} />
      {label}
      {children}
    </button>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
